import React, { useState } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { FEED_CATEGORIES, FORM_TYPES, shouldShowFormType } from './feedConstants';
import '../../../pages/BridgeClub/BridgeClubFeed.css';

interface CreateBinDialogProps {
  onClose: () => void;
  onCreated: () => void;
  onError?: (message: string) => void;
}

/**
 * Create Feed Bin dialog — extracted verbatim from BridgeClubFeed.tsx.
 * Owns its own form + saving state; signals the parent via onCreated (which the
 * Feed page wires to loadData) and surfaces failures through onError (the page's
 * error banner). Pure refactor: no behaviour change from the inline modal.
 */
const CreateBinDialog: React.FC<CreateBinDialogProps> = ({ onClose, onCreated, onError }) => {
  const [newBinName, setNewBinName] = useState('');
  const [newBinCategory, setNewBinCategory] = useState('other');
  const [newBinFormType, setNewBinFormType] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const handleCreateBin = async () => {
    if (!newBinName.trim()) return;
    setSaving(true);
    try {
      await bridgeClubAPI.createFeedBin({
        name: newBinName.trim(),
        feed_category: newBinCategory,
        form_type: newBinFormType || null,
      });
    } catch (err) {
      console.error('Failed to create bin:', err);
      onError?.('Failed to create bin');
      setSaving(false);
      return;
    }
    onCreated();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Create Feed Bin</h3>
        <div className="form-group">
          <label>Bin Name</label>
          <input
            type="text"
            value={newBinName}
            onChange={(e) => setNewBinName(e.target.value)}
            placeholder="e.g., Layer Feed Bin"
          />
        </div>
        <div className="form-group">
          <label>Feed Category</label>
          <div className="category-grid">
            {FEED_CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className={`category-btn ${newBinCategory === cat.value ? 'selected' : ''}`}
                onClick={() => setNewBinCategory(cat.value)}
              >
                <span className="cat-emoji">{cat.emoji}</span>
                <span className="cat-label">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
        {shouldShowFormType(newBinCategory) && (
          <div className="form-group">
            <label>Feed Form</label>
            <div className="form-type-grid">
              {FORM_TYPES.map(form => (
                <button
                  key={form.value}
                  className={`form-type-btn ${newBinFormType === form.value ? 'selected' : ''}`}
                  onClick={() => setNewBinFormType(newBinFormType === form.value ? '' : form.value)}
                >
                  <span className="form-icon">{form.icon}</span>
                  <span className="form-label">{form.label}</span>
                </button>
              ))}
            </div>
            <p className="form-help">What form of feed do you usually buy for this bin?</p>
          </div>
        )}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleCreateBin}
            disabled={saving || !newBinName.trim()}
          >
            {saving ? 'Creating...' : 'Create Bin'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateBinDialog;
